import { useMemo } from "react";
import max from "lodash/max";
import { BlobRow } from "../../types/supabase";
import { useFuseSearch } from "./useFuseSearch";
import { formatDateInTimeZone } from "../../lib/formatDateInTimeZone";
import { extractExpenseFromTextract } from "../../lib/util/extractExpenseFromTextract";
import { BlobModel } from "../reduxToolkit/blobsSlice";

interface ReceiptDocument extends BlobModel {
  longDate?: string | null
  textractProvider?: string | null
  textractAmount?: string | null
  textractDate?: string | null
  textractFormattedDate?: string | null
  textractSummary?: string | null
}

const searchKeys: Array<keyof ReceiptDocument> = [
  'file_name',
  'notes',
  'longDate',
  'textractProvider',
  'textractAmount',
  'textractDate',
  'textractFormattedDate',
  'textractSummary'
]

export function useReceiptSearch(
  receipts: Array<BlobModel>,
) {
  const updatedAt = max(receipts?.map((r) => r.updated_at)) || 0

  const docs: Array<ReceiptDocument> | undefined = useMemo(() => {
    return receipts?.map((receipt) => {
      const textractData = extractExpenseFromTextract(receipt)
      
      return {
        ...receipt,
        longDate: receipt.created_at ? formatDateInTimeZone(receipt.created_at, { format: 'MMMM dd, yyyy' }) : null,
        textractProvider: textractData?.provider || null,
        textractAmount: textractData?.listedAmount || textractData?.paidAmount || null,
        textractDate: textractData?.date || null,
        textractFormattedDate: textractData?.date ? formatDateInTimeZone(textractData.date, { format: 'MMMM dd, yyyy' }) : null,
        textractSummary: textractData?.summary || null,
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt])

  return useFuseSearch(
      docs || [],
      {
        findAllMatches: true,
        includeScore: true,
        keys: searchKeys,
        initialThreshold: 0.4,
        fallbackThreshold: 0.6,
      },
      [
        // Reindex the fuse search whenever the receipts change (i.e. the latest updated at changes)
        updatedAt,
      ]
    )
}